/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { convertKeysCase, convertToSnakeCase } from '@/account/lib/convertCase';
import { convertToBoolean } from './convertToBoolean';

const booleanList = ['true', 'false', 'yes', 'on', 'off'];

const dictionary: Record<string, string> = {
  firstName: '',
  lastName: '',
  city: '',
  company: '',
  countryCode: '',
  region: '',
  regionCode: '',
  regionId: '',
  id: '',
  telephone: '',
  vatId: '',
  postcode: '',
  defaultShipping: '',
  defaultBilling: '',
  street: '',
  saveAddressBook: '',
  prefix: '',
  middleName: '',
  fax: '',
  suffix: '',
  uid: '',
} as const;

export const groupMultilineFields = (
  inputObj: Record<string, any>
): Record<string, any> => {
  const outputObj: Record<string, any> = {};
  const multilineFields: Record<
    string,
    Array<{ index: number; value: any }>
  > = {};

  for (const key in inputObj) {
    if (Object.prototype.hasOwnProperty.call(inputObj, key)) {
      const value = inputObj[key];
      const match = key.match(/^(.*)Multiline_(\d+)$/);

      if (match) {
        const baseKey: string = match[1];
        const index: number = parseInt(match[2], 10);

        if (!multilineFields[baseKey]) {
          multilineFields[baseKey] = [];
        }

        multilineFields[baseKey].push({ index, value });
      } else {
        const relatedMultilineKeys = Object.keys(inputObj).filter((k) =>
          k.startsWith(`${key}Multiline_`)
        );
        if (relatedMultilineKeys.length > 0) {
          multilineFields[key] = [{ index: 1, value }];
        } else {
          outputObj[key] = value;
        }
      }
    }
  }

  for (const baseKey in multilineFields) {
    if (Object.prototype.hasOwnProperty.call(multilineFields, baseKey)) {
      const entries = multilineFields[baseKey];
      entries.sort((a, b) => a.index - b.index);
      outputObj[baseKey] = entries.map((entry) => entry.value);
    }
  }

  return outputObj;
};

export const separateKeys = (
  inputData: Record<string, any>
): Record<keyof typeof dictionary, unknown> => {
  const defaultFields: Record<string, any> = {};
  const customAttributes: Record<string, string | boolean | number>[] = [];

  for (const key in inputData) {
    const value = booleanList.includes(inputData[key])
      ? convertToBoolean(inputData[key])
      : inputData[key];

    if (Object.prototype.hasOwnProperty.call(inputData, key)) {
      if (Object.prototype.hasOwnProperty.call(dictionary, key)) {
        defaultFields[key] = value;
      } else {
        customAttributes.push({ code: convertToSnakeCase(key), value });
      }
    }
  }

  return { ...defaultFields, customAttributes };
};

export default (fields?: Record<string, any>, isForm = false) => {
  const newFields = convertKeysCase(fields, 'camelCase', {
    firstname: 'firstName',
    lastname: 'lastName',
    middlename: 'middleName',
  });

  const result = separateKeys(groupMultilineFields(newFields));

  if (!isForm) return result;

  const [regionCode, regionId] = result.region
    ? (result.region as string).split(',')
    : [];

  return {
    ...result,
    region: {
      regionCode,
      ...(regionId && { regionId: +regionId }),
    },
  };
};
